// pages/superadmin/SectionsPage.tsx
import React, { useState, useEffect } from "react";
import { Section, SectionForm, Exam, Trait, SectionTrait, QuestionTypeMaster } from "../../types";
import { useApp } from "../../context/AppContext";
import {
  getSections,
  createSection,
  updateSection,
  deleteSection as deleteSectionService,
  assignSectionToExam,
} from "../../services/sectionService";
import { getTraits } from "../../services/traitService";
import { getExams } from "../../services/examService";
import { getQuestionTypes, getQuestionTypesBySectionType } from "../../services/questionTypeService";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import { Input, Textarea, Checkbox } from "../../components/ui/FormFields";
import MultiSelect from "../../components/ui/MultiSelect";
import { toast } from "react-hot-toast";

export type SectionType = 'psychometric' | 'aptitude' | 'hybrid';

export const SECTION_TYPES: { value: SectionType; label: string; icon: string; description: string }[] = [
  {
    value: 'psychometric',
    label: 'Psychometric',
    icon: '🧠',
    description: 'Trait-based questions with Likert scales'
  },
  {
    value: 'aptitude',
    label: 'Aptitude',
    icon: '📝',
    description: 'Single or multiple correct answer questions'
  },
  {
    value: 'hybrid',
    label: 'Hybrid',
    icon: '🎲',
    description: 'Mix of different question types with traits'
  },
];

const BLANK_SECTION: SectionForm = {
  name: "",
  description: "",
  instructions: "",
  order: 1,
  traits: [],
  scaleType: "",
  questionCount: 15,
  totalQuestions: 15,
  minQuestions: 10,
  selectionType: "random",
  duration: 15,
  timeLimit: 15,
  autoSubmit: true,
  weightage: 100,
  marksPerQuestion: 0,
  negativeMarking: false,
  isOptional: false,
  passingScore: 0,
  sectionType: "psychometric",
  allowedQuestionTypes: [],
};

const SectionsPage: React.FC = () => {
  const { setExams: setContextExams } = useApp();
  const [sections, setSections] = useState<Section[]>([]);
  const [exams, setExamsList] = useState<Exam[]>([]);
  const [traits, setTraits] = useState<Trait[]>([]);
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editSection, setEditSection] = useState<Section | null>(null);
  const [deleteSectionItem, setDeleteSectionItem] = useState<Section | null>(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedSectionForAssign, setSelectedSectionForAssign] = useState<Section | null>(null);
  const [selectedExamId, setSelectedExamId] = useState("");
  const [selectedSectionType, setSelectedSectionType] = useState<SectionType>('psychometric');
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState<QuestionTypeMaster[]>([]);
  const [form, setForm] = useState<SectionForm>(BLANK_SECTION);
  const [selectedTraitIds, setSelectedTraitIds] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadAvailableQuestionTypes();
  }, [selectedSectionType]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedSections, fetchedExams, fetchedTraits, fetchedQuestionTypes] = await Promise.all([
        getSections(),
        getExams(),
        getTraits(),
        getQuestionTypes(false)
      ]);
      setSections(fetchedSections);
      setExamsList(fetchedExams);
      setTraits(fetchedTraits);
      setQuestionTypes(fetchedQuestionTypes);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load sections data");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableQuestionTypes = async () => {
    const types = await getQuestionTypes(false); // ✅ get ALL types
    setAvailableQuestionTypes(types);
  };

  const handleFormChange = (k: keyof SectionForm) => (v: any) => {
    let value = v;

    if (k === "order" || k === "questionCount" || k === "totalQuestions" || k === "minQuestions" ||
      k === "duration" || k === "timeLimit" || k === "weightage" || k === "passingScore" ||
      k === "marksPerQuestion") {
      value = v === "" ? 0 : Number(v);
    }

    setForm(f => ({ ...f, [k]: value }));
  };

  const handleTraitSelect = (traitIds: string[]) => {
    setSelectedTraitIds(traitIds);

    const selectedTraitsData = traits
      .filter(t => traitIds.includes(t.id))
      .map((t, index) => ({
        traitId: t.id,
        traitName: t.name,
        traitIcon: "📊",
        scaleId: "",
        scaleType: t.scaleType || "likert_5",
        weightage: traitIds.length > 0 ? Math.floor(100 / traitIds.length) : 0,
        order: index + 1
      }));

    const scaleType = selectedTraitsData.length > 0 ? selectedTraitsData[0].scaleType : "";

    setForm(prev => ({
      ...prev,
      traits: selectedTraitsData,
      scaleType
    }));
  };

  const handleQuestionTypeSelect = (questionTypeCodes: string[]) => {
    setForm(f => ({ ...f, allowedQuestionTypes: questionTypeCodes }));
  };

  const handleSectionTypeChange = (type: SectionType) => {
    setSelectedSectionType(type);
    setSelectedTraitIds([]);
    setForm(f => ({
      ...f,
      sectionType: type,
      traits: [],
      scaleType: "",
      allowedQuestionTypes: []
    }));
  };

  const openNew = () => {
    setEditSection(null);
    setSelectedSectionType('psychometric');
    setSelectedTraitIds([]);
    setForm({
      ...BLANK_SECTION,
      order: sections.length + 1,
      weightage: 100,
      allowedQuestionTypes: [],
      traits: [],
      totalQuestions: 15,
      minQuestions: 10,
      selectionType: "random",
      timeLimit: 15,
      autoSubmit: true,
      marksPerQuestion: 0,
      negativeMarking: false
    });
    setShowModal(true);
  };

  const openEdit = (s: Section) => {
    setEditSection(s);
    const sectionType = (s.sectionType === 'mixed' ? 'hybrid' : s.sectionType) as SectionType;
    setSelectedSectionType(sectionType);
    const traitIds = (s.traits || []).map(t => t.traitId);
    setSelectedTraitIds(traitIds);
    setForm({
      name: s.name,
      description: s.description || "",
      instructions: s.instructions || "",
      order: s.order,
      traits: s.traits || [],
      scaleType: s.scaleType || "",
      questionCount: s.questionCount || s.totalQuestions || 15,
      totalQuestions: s.totalQuestions || s.questionCount || 15,
      minQuestions: s.minQuestions || 10,
      selectionType: s.selectionType || "random",
      duration: s.duration || s.timeLimit || 15,
      timeLimit: s.timeLimit || s.duration || 15,
      autoSubmit: s.autoSubmit !== undefined ? s.autoSubmit : true,
      weightage: s.weightage,
      isOptional: s.isOptional || false,
      passingScore: s.passingScore || 0,
      sectionType: sectionType,
      allowedQuestionTypes: s.allowedQuestionTypes || [],
      marksPerQuestion: s.marksPerQuestion || 0,
      negativeMarking: s.negativeMarking || false
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      if (!form.name || form.name.trim() === "") {
        toast.error("Section name is required");
        return;
      }

      if (form.totalQuestions <= 0) {
        toast.error("Total questions must be greater than 0");
        return;
      }

      if (form.minQuestions > form.totalQuestions) {
        toast.error("Minimum questions cannot exceed total questions");
        return;
      }

      if (form.timeLimit <= 0) {
        toast.error("Time limit must be greater than 0");
        return;
      }

      if (form.weightage <= 0 || form.weightage > 100) {
        toast.error("Weightage must be between 1 and 100");
        return;
      }

      if ((selectedSectionType === 'psychometric' || selectedSectionType === 'hybrid') && (!form.traits || form.traits.length === 0)) {
        toast.error(`Please select at least one trait for ${selectedSectionType} section`);
        return;
      }

      if (form.allowedQuestionTypes.length === 0) {
        toast.error("Please select at least one allowed question type");
        return;
      }

      const sectionData = {
        name: form.name.trim(),
        description: form.description || "",
        instructions: form.instructions || "",
        order: Number(form.order) || 1,
        traits: form.traits.map((t, index) => ({
          traitId: t.traitId,
          traitName: t.traitName,
          traitIcon: t.traitIcon || "📌",
          scaleId: t.scaleId || "",
          scaleType: t.scaleType,
          weightage: t.weightage || Math.floor(100 / form.traits.length),
          order: t.order || index + 1
        })),
        scaleType: form.scaleType || form.traits[0]?.scaleType || "",
        totalQuestions: Number(form.totalQuestions) || 0,
        minQuestions: Number(form.minQuestions) || 0,
        selectionType: form.selectionType || "random",
        timeLimit: Number(form.timeLimit) || 0,
        autoSubmit: Boolean(form.autoSubmit),
        weightage: Number(form.weightage) || 0,
        marksPerQuestion: Number(form.marksPerQuestion) || 0,
        negativeMarking: Boolean(form.negativeMarking),
        isOptional: Boolean(form.isOptional),
        passingScore: Number(form.passingScore) || 0,
        sectionType: selectedSectionType === 'hybrid' ? 'mixed' : selectedSectionType,
        allowedQuestionTypes: form.allowedQuestionTypes,
      };

      if (editSection) {
        await updateSection(editSection.id, sectionData);
        toast.success("Section updated successfully");
      } else {
        await createSection(sectionData);
        toast.success("Section created successfully");
      }

      await loadData();
      setShowModal(false);
      setForm(BLANK_SECTION);
      setSelectedSectionType('psychometric');
      setSelectedTraitIds([]);
    } catch (error: any) {
      console.error("Error saving section:", error);
      toast.error(error.message || "Failed to save section");
    }
  };

  const confirmDelete = async () => {
    if (!deleteSectionItem) return;
    try {
      await deleteSectionService(deleteSectionItem.id);
      toast.success("Section deleted successfully");
      await loadData();
      setDeleteSectionItem(null);
    } catch (error) {
      console.error("Error deleting section:", error);
      toast.error("Failed to delete section");
    }
  };

  const openAssignModal = (section: Section) => {
    setSelectedSectionForAssign(section);
    setSelectedExamId("");
    setAssignModalOpen(true);
  };

  const assignSectionToExamHandler = async () => {
    if (!selectedSectionForAssign || !selectedExamId) {
      toast.error("Please select an exam");
      return;
    }

    try {
      await assignSectionToExam(selectedExamId, selectedSectionForAssign.id);
      toast.success("Section assigned to exam successfully");
      await loadData();
      setAssignModalOpen(false);
      setSelectedSectionForAssign(null);
      setSelectedExamId("");
    } catch (error) {
      console.error("Error assigning section:", error);
      toast.error("Failed to assign section to exam");
    }
  };

  const getAvailableExamsForSection = (sectionId: string) => {
    return exams.filter(exam => !exam.sections?.includes(sectionId));
  };

  const getExamsWithSection = (sectionId: string) => {
    return exams.filter(exam => exam.sections?.includes(sectionId));
  };

  const getSectionTypeIcon = (type: string) => {
    const normalizedType = type === 'mixed' ? 'hybrid' : type;
    const found = SECTION_TYPES.find(t => t.value === normalizedType);
    return found ? found.icon : "📦";
  };

  const getSectionTypeLabel = (type: string) => {
    const normalizedType = type === 'mixed' ? 'hybrid' : type;
    const found = SECTION_TYPES.find(t => t.value === normalizedType);
    return found ? found.label : type;
  };

  const getQuestionTypeLabel = (code: string) => {
    const found = questionTypes.find(t => t.code === code);
    return found ? found.name : code;
  };

  const traitOptions = traits.map(trait => ({
    value: trait.id,
    label: `${'🧠'} ${trait.name} (${trait.scaleType || 'No scale'})`
  }));

  const questionTypeOptions = availableQuestionTypes.map(qt => ({
    value: qt.code,
    label: `${qt.name}`
  }));

  const allQuestionTypeOptions = questionTypes.map(qt => ({
    value: qt.code,
    label: `${qt.name}`
  }));

  const columns: Column<Section>[] = [
    {
      key: "name",
      label: "Section Name",
      render: (v, item) => (
        <div>
          <div className="font-semibold text-gray-800">{v}</div>
          {item.instructions && (
            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{item.instructions}</div>
          )}
        </div>
      )
    },
    {
      key: "sectionType",
      label: "Section Type",
      render: (_, item) => {
        const type = item.sectionType === 'mixed' ? 'hybrid' : item.sectionType;
        return (
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${type === 'psychometric' ? 'bg-purple-100 text-purple-700' :
            type === 'aptitude' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`}>
            <span>{getSectionTypeIcon(type)}</span>
            <span>{getSectionTypeLabel(type)}</span>
          </span>
        );
      }
    },
    {
      key: "totalQuestions",
      label: "Questions",
      render: (_, item) => (
        <div className="text-sm">
          <span className="font-medium">{item.totalQuestions || item.questionCount || 0}</span>
          {(item.minQuestions !== undefined && item.minQuestions > 0) &&
            item.minQuestions < (item.totalQuestions || item.questionCount || 0) && (
              <span className="text-xs text-gray-500 block">Min: {item.minQuestions}</span>
            )}
        </div>
      )
    },
    {
      key: "timeLimit",
      label: "Time Limit",
      render: (v, item) => (
        <span>{item.timeLimit || item.duration} mins {item.autoSubmit && "(Auto)"}</span>
      )
    },
    {
      key: "traits",
      label: "Traits",
      render: (v: SectionTrait[]) => {
        if (!v || v.length === 0) return <span className="text-xs text-gray-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {v.slice(0, 2).map(trait => (
              <span key={trait.traitId} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                {trait.traitName}
              </span>
            ))}
            {v.length > 2 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                +{v.length - 2}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: "assignedExams",
      label: "Exams",
      render: (_, item) => {
        const assignedExams = getExamsWithSection(item.id);
        if (assignedExams.length === 0) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {assignedExams.slice(0, 2).map(exam => (
              <span key={exam.id} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                {exam.title.length > 15 ? exam.title.slice(0, 12) + '...' : exam.title}
              </span>
            ))}
            {assignedExams.length > 2 && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                +{assignedExams.length - 2}
              </span>
            )}
          </div>
        );
      }
    },
    { key: "weightage", label: "Wtg", render: (v) => <span className="font-medium">{v}%</span> },
    {
      key: "actions",
      label: "",
      render: (_, item) => (
        <div className="flex gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => openAssignModal(item)}
            className="text-xs px-2 py-1"
          >
            Assign
          </Button>
        </div>
      )
    }
  ];

  const selectedScaleTypes = Array.from(new Set(form.traits.map(t => t.scaleType)));
  const hasScaleConflict = selectedScaleTypes.length > 1;
  const isPsychometric = selectedSectionType === 'psychometric';
  const isHybrid = selectedSectionType === 'hybrid';
  const showTraitsSection = isPsychometric || isHybrid;

  return (
    <PageShell
      title="Section Master"
      subtitle="Create and manage assessment sections"
      action={{ label: "Create Section", onClick: openNew }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Sections" value={sections.length} icon="📂" color="#6366f1" />
        <StatCard label="Psychometric" value={sections.filter(s => s.sectionType === 'psychometric' || s.sectionType === 'psychometric').length} icon="🧠" color="#8b5cf6" />
        <StatCard label="Aptitude" value={sections.filter(s => s.sectionType === 'aptitude').length} icon="📝" color="#3b82f6" />
        <StatCard label="Hybrid" value={sections.filter(s => s.sectionType === 'hybrid' || s.sectionType === 'mixed').length} icon="🎲" color="#f59e0b" />
      </div>

      <GlassCard className="p-5">
        <Table
          columns={columns}
          data={sections}
          onEdit={openEdit}
          onDelete={(s) => setDeleteSectionItem(s)}
        />
        {sections.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No sections found. Click "Create Section" to create your first section.
          </div>
        )}
      </GlassCard>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editSection ? "Edit Section" : "Create New Section"}
        size="xl"
      >
        <div className="space-y-5">
          {/* Section Type */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">🎯</span>
                Section Type
              </h3>
            </div>
            <div className="p-5">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={selectedSectionType}
                onChange={(e) => handleSectionTypeChange(e.target.value as SectionType)}
              >
                {SECTION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">📝</span>
                Basic Information
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Input
                label="Section Name"
                value={form.name}
                onChange={handleFormChange("name")}
                required
                placeholder="e.g., Emotional Intelligence Assessment"
              />
              <Textarea
                label="Instructions"
                value={form.instructions}
                onChange={handleFormChange("instructions")}
                placeholder="Instructions for candidates attempting this section..."
                rows={3}
              />
            </div>
          </div>

          {/* Allowed Question Types */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">📋</span>
                Allowed Question Types
              </h3>
              <p className="text-xs text-gray-500 mt-1">Select question types that can be used in this section</p>
            </div>
            <div className="p-5">
              <MultiSelect
                label="Question Type"
                options={selectedSectionType === 'aptitude' ? allQuestionTypeOptions : questionTypeOptions}
                selectedValues={form.allowedQuestionTypes}
                onChange={handleQuestionTypeSelect}
                placeholder="Select question types..."
                required
              />
            </div>
          </div>

          {/* Trait Mapping (for Psychometric & Hybrid) */}
          {showTraitsSection && (
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-xl">🧠</span>
                  Trait Mapping
                </h3>
                <p className="text-xs text-gray-500 mt-1">Select traits to measure in this section</p>
              </div>
              <div className="p-5">
                <MultiSelect
                  label="Traits"
                  options={traitOptions}
                  selectedValues={selectedTraitIds}
                  onChange={handleTraitSelect}
                  placeholder="Select traits..."
                  required
                />

                {hasScaleConflict && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700 font-medium">⚠️ Scale Conflict Detected</p>
                    <p className="text-xs text-red-600 mt-1">
                      Multiple scale types detected: {selectedScaleTypes.join(", ")}. All traits must use the same scale type.
                    </p>
                  </div>
                )}

                {form.traits.length > 0 && !hasScaleConflict && form.scaleType && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 font-medium">✓ Scale Type: {form.scaleType}</p>
                    <p className="text-xs text-green-600 mt-1">
                      This scale type will be used for all questions in this section.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Question Configuration */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">❓</span>
                Question Configuration
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Total Questions"
                  type="number"
                  value={String(form.totalQuestions)}
                  onChange={handleFormChange("totalQuestions")}
                  required
                  min="1"
                  placeholder="15"
                />
                <Input
                  label="Minimum Questions"
                  type="number"
                  value={String(form.minQuestions)}
                  onChange={handleFormChange("minQuestions")}
                  min="1"
                  placeholder="10"
                  helperText="Minimum questions to be answered"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Selection Type</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={form.selectionType}
                    onChange={(e) => handleFormChange("selectionType")(e.target.value)}
                  >
                    <option value="random">Random</option>
                    <option value="sequential">Sequential</option>
                    <option value="adaptive">Adaptive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Timing */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">⏱️</span>
                Timing
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Time Limit (minutes)"
                  type="number"
                  value={String(form.timeLimit)}
                  onChange={handleFormChange("timeLimit")}
                  required
                  min="1"
                  placeholder="15"
                />
                <div className="flex items-end">
                  <Checkbox
                    label="Auto Submit (Auto submit when time expires)"
                    checked={form.autoSubmit}
                    onChange={(checked) => handleFormChange("autoSubmit")(checked)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Scoring */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">🎯</span>
                Scoring
              </h3>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Weightage (%)"
                  type="number"
                  value={String(form.weightage)}
                  onChange={handleFormChange("weightage")}
                  step="1"
                  min="1"
                  max="100"
                  required
                  placeholder="100"
                />
                <Input
                  label="Passing Score (%)"
                  type="number"
                  value={String(form.passingScore)}
                  onChange={handleFormChange("passingScore")}
                  min="0"
                  max="100"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 rounded-xl border border-blue-200">
            <div className="px-5 py-3">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Section Type Information:</strong><br />
                {selectedSectionType === 'psychometric' && "Psychometric sections use Likert scale questions linked to traits. Marks per question and negative marking are not applicable."}
                {selectedSectionType === 'aptitude' && "Aptitude sections use objective questions (MCQ, MSQ, etc.). You can configure marks per question and negative marking."}
                {selectedSectionType === 'hybrid' && "Hybrid sections can include both psychometric (with traits) and objective questions."}
              </p>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-gradient-to-r from-indigo-600 to-purple-600"
              disabled={
                !form.name.trim() ||
                form.totalQuestions <= 0 ||
                form.minQuestions > form.totalQuestions ||
                form.timeLimit <= 0 ||
                form.weightage <= 0 ||
                form.weightage > 100 ||
                (showTraitsSection && (hasScaleConflict || form.traits.length === 0)) ||
                form.allowedQuestionTypes.length === 0
              }
            >
              {editSection ? "Update Section" : "Create Section"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Assign Section to Exam"
        size="md"
      >
        <div className="space-y-5">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="font-semibold text-gray-800">{selectedSectionForAssign?.name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Type: {getSectionTypeLabel(selectedSectionForAssign?.sectionType || '')}
            </p>
            {selectedSectionForAssign?.traits && selectedSectionForAssign.traits.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Traits: {selectedSectionForAssign.traits.map(t => t.traitName).join(', ')}
              </p>
            )}
          </div>

          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={selectedExamId}
            onChange={(e) => setSelectedExamId(e.target.value)}
            required
          >
            <option value="">— Select an exam —</option>
            {getAvailableExamsForSection(selectedSectionForAssign?.id || "").map(e => (
              <option key={e.id} value={e.id}>
                {e.title}
              </option>
            ))}
          </select>

          {getAvailableExamsForSection(selectedSectionForAssign?.id || "").length === 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-700">
                ⚠️ This section is already assigned to all available exams.
              </p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={assignSectionToExamHandler}
              className="bg-gradient-to-r from-indigo-600 to-purple-600"
              disabled={!selectedExamId}
            >
              Assign to Exam
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteSectionItem}
        onClose={() => setDeleteSectionItem(null)}
        onConfirm={confirmDelete}
        title="Delete Section"
        message={`Are you sure you want to delete section "${deleteSectionItem?.name}"? This will also remove it from all exams it's assigned to.`}
      />
    </PageShell>
  );
};

export default SectionsPage;